import { useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ChartCrosshairAxisLabel } from './ChartCrosshairAxisLabel';
import type { ChartPlot, ChartPriceLine, ChartPriceLineStyle } from './types';
import { invertLinearScale, type LinearScale } from './utils/scales';

type ChartPriceLinesProps = {
  lines: ChartPriceLine[];
  plot: ChartPlot;
  yDomain: [number, number];
  yScale: LinearScale;
  /** SVG viewport height, used to map pointer positions into SVG space while dragging. */
  height: number;
  formatValue?: (value: number) => string;
};

const DRAG_HANDLE_WIDTH = 12;
const LABEL_OFFSET = 4;

export const hasInteractivePriceLines = (lines: ChartPriceLine[] | undefined) =>
  lines?.some((line) => line.onValueChange || line.onValueCommit || line.action) ?? false;

/** Dash patterns scale with stroke width so thicker lines keep the same rhythm. */
const getDashArray = (style: ChartPriceLineStyle, width: number) => {
  switch (style) {
    case 'solid':
      return undefined;
    case 'dashed':
      return `${width * 4} ${width * 4}`;
    case 'dotted':
      return `${width} ${width * 2}`;
    default: {
      const exhaustive: never = style;
      return exhaustive;
    }
  }
};

const getLabelY = (
  position: NonNullable<ChartPriceLine['labelPosition']>,
  lineY: number,
  gap: number,
  labelHeight: number,
) => {
  switch (position) {
    case 'above':
      return lineY - gap - labelHeight;
    case 'below':
      return lineY + gap;
    case 'center':
      return lineY - labelHeight / 2;
    default: {
      const exhaustive: never = position;
      return exhaustive;
    }
  }
};

const LABEL_HEIGHT = 16;
const LABEL_WIDTH_DEFAULT = 48;

type ChartPriceLineLabelProps = {
  text: ReactNode;
  color: string;
  background: string;
  plot: ChartPlot;
  /** Y of the price line; the label sits `gap` px above or below it, or centered on it. */
  lineY: number;
  gap: number;
  position: NonNullable<ChartPriceLine['labelPosition']>;
  align: NonNullable<ChartPriceLine['labelAlign']>;
  offset: number;
  /** SVG viewport height, used to keep the label off the top and bottom edges. */
  height: number;
  action?: ChartPriceLine['action'];
  disabled?: boolean;
};

/** Caption on a translucent pill so it stays legible over candles. */
const ChartPriceLineLabel = ({
  text,
  color,
  background,
  plot,
  lineY,
  gap,
  position,
  align,
  offset,
  height,
  action,
  disabled,
}: ChartPriceLineLabelProps) => {
  const [width, setWidth] = useState(LABEL_WIDTH_DEFAULT);
  const textRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const textWidth = textRef.current?.offsetWidth;
    if (!textWidth) return;

    const nextWidth = Math.ceil(textWidth);
    setWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
  }, [text]);

  const labelHeight = action ? 24 : LABEL_HEIGHT;
  const labelWidth = width + (action ? 24 : 0);
  const shift = offset * plot.width;
  const alignedX =
    align === 'left'
      ? plot.left + LABEL_OFFSET + shift
      : align === 'center'
        ? plot.left + (plot.width - labelWidth) / 2 + shift
        : plot.right - LABEL_OFFSET - labelWidth - shift;
  const x = Math.min(Math.max(alignedX, plot.left), plot.right - labelWidth);
  // The plot's vertical padding is shorter than the label, so a line resting against
  // the top or bottom of the plot would push its label off the viewport.
  const y = Math.min(
    Math.max(getLabelY(position, lineY, gap, labelHeight), 0),
    height - labelHeight,
  );

  return (
    <g data-testid="chart-price-line-label" pointerEvents="none">
      {/* Theme-aware hairline (dark on light, light on dark) so overlapping pills stay distinct. */}
      <rect
        x={x}
        y={y}
        width={labelWidth}
        height={labelHeight}
        rx={3}
        fill={background}
        stroke="var(--color-token-alphalight-500)"
        strokeWidth={1}
      />
      <foreignObject x={x} y={y} width={width} height={labelHeight}>
        {/* `shrink-0` + nowrap keep the measured width honest while the box is still the default size. */}
        <div
          ref={textRef}
          className={fontStyles({
            variant: 'CAPTION',
            class: [
              'flex',
              'h-full',
              'w-fit',
              'shrink-0',
              'items-center',
              'gap-1',
              'px-1',
              'whitespace-nowrap',
            ],
          })}
          style={{ color }}
        >
          {text}
        </div>
      </foreignObject>
      {action && (
        <foreignObject x={x + width} y={y} width={24} height={24} pointerEvents="auto">
          <button
            type="button"
            aria-label={action.label}
            title={action.label}
            aria-busy={action.pending || undefined}
            disabled={disabled}
            className={buttons({
              size: 'extraSmall',
              color: 'alphaGray',
              shape: 'roundedSubtle',
              class: 'w-6 p-0',
            })}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              action.onClick();
            }}
          >
            <span aria-hidden="true">{action.pending ? '…' : '×'}</span>
          </button>
        </foreignObject>
      )}
    </g>
  );
};

type Drag = { key: string; pointerId: number; startValue: number; value?: number };

export const ChartPriceLines = ({
  lines,
  plot,
  yDomain,
  yScale,
  height,
  formatValue,
}: ChartPriceLinesProps) => {
  const [drag, setDrag] = useState<Drag>();

  const toValue = (event: PointerEvent<SVGLineElement>) => {
    const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!bounds || bounds.height === 0) return undefined;
    const svgY = ((event.clientY - bounds.top) / bounds.height) * height;
    const clampedY = Math.min(Math.max(svgY, plot.top), plot.bottom);
    return invertLinearScale(clampedY, yDomain, [plot.bottom, plot.top]);
  };

  const placed = lines.flatMap((line) => {
    const disabled = line.disabled || line.action?.pending;
    // Commit lines preview the drag locally; change lines are controlled by the consumer.
    const value =
      !disabled && line.onValueCommit && drag?.key === line.key && drag.value !== undefined
        ? drag.value
        : line.value;
    const y = yScale(value);
    if (!Number.isFinite(value) || y < plot.top || y > plot.bottom) return [];
    return [{ line, value, y, disabled }];
  });

  // A line that is disabled or removed mid-drag unmounts its handle, so the drop never arrives.
  const dragged = placed.find(({ line }) => line.key === drag?.key);
  const dragStale = drag !== undefined && (!dragged || dragged.disabled);
  useLayoutEffect(() => {
    if (dragStale) setDrag(undefined);
  }, [dragStale]);

  return (
    <>
      {placed.map(({ line, value, y, disabled }) => {
        const { lineStyle = 'dashed', lineWidth = 1 } = line;
        return (
          <g key={line.key} data-testid="chart-price-line">
            <line
              x1={plot.left}
              x2={plot.right}
              y1={y}
              y2={y}
              stroke={line.color}
              strokeWidth={lineWidth}
              strokeDasharray={getDashArray(lineStyle, lineWidth)}
              pointerEvents="none"
            />
            {(line.onValueChange || line.onValueCommit) && !disabled && (
              <line
                data-testid="chart-price-line-handle"
                x1={plot.left}
                x2={plot.right}
                y1={y}
                y2={y}
                stroke="transparent"
                strokeWidth={Math.max(DRAG_HANDLE_WIDTH, lineWidth)}
                className="cursor-ns-resize touch-none"
                onPointerDown={(event) => {
                  if (event.button !== 0 || drag) return;
                  event.stopPropagation();
                  event.preventDefault();
                  event.currentTarget.setPointerCapture?.(event.pointerId);
                  setDrag({ key: line.key, pointerId: event.pointerId, startValue: line.value });
                }}
                onPointerMove={(event) => {
                  if (drag?.key !== line.key || drag.pointerId !== event.pointerId) return;
                  event.stopPropagation();
                  const nextValue = toValue(event);
                  if (nextValue == null || !Number.isFinite(nextValue)) return;
                  if (line.onValueCommit) setDrag({ ...drag, value: nextValue });
                  else line.onValueChange?.(nextValue);
                }}
                onPointerUp={(event) => {
                  if (drag?.key !== line.key || drag.pointerId !== event.pointerId) return;
                  event.stopPropagation();
                  event.currentTarget.releasePointerCapture?.(event.pointerId);
                  setDrag(undefined);
                  if (drag.value != null && drag.value !== drag.startValue) {
                    line.onValueCommit?.(drag.value);
                  }
                }}
                onPointerCancel={() => setDrag(undefined)}
                onLostPointerCapture={() => setDrag(undefined)}
              />
            )}
            {formatValue && (
              <ChartCrosshairAxisLabel
                axis="y"
                plot={plot}
                position={y}
                label={formatValue(value)}
                fill={line.color}
                textFill="var(--primitives-color-gray-100)"
              />
            )}
          </g>
        );
      })}
      {/* SVG paints in document order, so every label goes after every line. */}
      {placed.map(
        ({ line, y, disabled }) =>
          (line.label || line.action) && (
            <ChartPriceLineLabel
              key={line.key}
              text={line.label ?? ''}
              color={line.labelColor ?? line.color}
              background={line.labelBackground ?? 'var(--color-token-alphalight-500)'}
              plot={plot}
              lineY={y}
              gap={LABEL_OFFSET + (line.lineWidth ?? 1) / 2}
              position={line.labelPosition ?? 'above'}
              align={line.labelAlign ?? 'right'}
              offset={line.labelOffset ?? 0}
              height={height}
              action={line.action}
              disabled={disabled}
            />
          ),
      )}
    </>
  );
};
